<template>
  <div
    class="lh-article-card-controls"
    :class="controlsClass"
  >
    <FavoriteDropDown
      class="lh-article-card-controls__favorite"
      :allowed="Boolean(user)"
      :favorite="favorite"
      :folders="folders.items"
      :icon-css-variables="favoriteIconCssVariables"
      @favorite-delete="onFavoriteDeleteEmit"
      @favorite-create-folder-and-add="onFavoriteCreateFolderAndAddEmit"
      @favorite-create-folder-and-transfer="onFavoriteCreateFolderAndTransferEmit"
      @favorite-transfer-to-folder="onFavoriteTransferToFolderEmit"
      @favorite-add-to-folder="onFavoriteAddToFolderEmit"
      @favorite-deferred="onFavoriteDeferredEmit"
      @favorite-action="onFavoriteAction"
      @open-favorite-menu="onOpenFavoriteMenu"
      @close-favorite-menu="onCloseFavoriteMenu"
    />

    <TheLinkUi
      v-if="commentsEnabled"
      class="lh-article-card-controls__comments"
      data-jest="comments"
      name="Открыть комментарии"
      :href="commentsUrl"
      @click="onClickComments(commentsUrl)"
    >
      <SvgAsset
        class="icon"
        :content="commentIconContent"
      />
      <span v-show="isHasComments">{{ commentsCount }}</span>
    </TheLinkUi>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { User } from '@devhacker/shared/features/user/types/user';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import TheLinkUi from '@/components/lh-ui/TheLinkUi/TheLink.vue';
  import { removeLifehackerFromUrl } from '~/utils/removeLifehackerFromUrl';
  import commentIconContent from '@/components/lh-ui/_assets/comment.svg?raw';
  import FavoriteDropDown from '~/components/FavoriteDropDown/FavoriteDropDown.vue';

  export default defineNuxtComponent({
    name: 'ArticleCardControls',
    components: {
      FavoriteDropDown,
      SvgAsset,
      TheLinkUi,
    },
    props: {
      url: {
        type: String as PropType<string>,
        required: true,
      },
      commentsEnabled: {
        type: Boolean as PropType<boolean>,
        required: true,
      },
      commentsCount: {
        type: Number as PropType<number>,
        required: false,
        default: 0,
      },
      user: {
        type: Object as PropType<User | null>,
        default: null,
      },
      favorite: {
        required: false,
        type: Object as PropType<IFavoriteItem | null>,
        default: null,
      },
      folders: {
        required: true,
        type: Object as PropType<IFolders>,
      },
      internalUrl: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
      },
      favoriteIconCssVariables: {
        type: Object as PropType<Record<string, string>>,
        default: null,
      },
    },
    data: () => ({
      activeCardControls: false,
    }),
    computed: {
      commentsUrl() {
        return removeLifehackerFromUrl(this.url) + '#comments';
      },
      commentIconContent: () => commentIconContent,
      isHasComments(): boolean {
        return this.commentsCount > 0;
      },
      controlsClass(): any {
        return this.activeCardControls ? 'lh-article-card-controls--active' : null;
      },
    },
    methods: {
      onOpenFavoriteMenu(): void {
        this.activeCardControls = true;
      },
      onCloseFavoriteMenu(): void {
        this.activeCardControls = false;
      },
      onFavoriteAction(): void {
        this.$emit('favorite-action');
      },
      onClickComments(link: string): void {
        this.$emit('clicked-on-comments', link);
      },
      onFavoriteDeferredEmit(deferredAction: Function, ctx: any) {
        this.$emit('favorite-deferred', deferredAction, ctx);
      },
      onFavoriteAddToFolderEmit(folder: IFolderItem) {
        this.$emit('favorite-add-to-folder', folder);
      },
      onFavoriteTransferToFolderEmit(favorite: IFavoriteItem, folder: IFolderItem) {
        this.$emit('favorite-transfer-to-folder', favorite, folder);
      },
      onFavoriteCreateFolderAndTransferEmit(nameFolder: string, favorite: IFavoriteItem): void {
        this.$emit('favorite-create-folder-and-transfer', nameFolder, favorite);
      },
      onFavoriteCreateFolderAndAddEmit(nameFolder: string): void {
        this.$emit('favorite-create-folder-and-add', nameFolder);
      },
      onFavoriteDeleteEmit(favorite: IFavoriteItem): void {
        this.$emit('favorite-delete', favorite);
      },
    },
  });
</script>

<style lang="scss">
  .lh-article-card-controls {
    $controls: &;

    @include fontSmallText;
    position: relative;

    display: flex;
    align-items: flex-start;
    min-width: min-content;

    // увеличиваем z-index чтобы выпадающее меню FavoriteBlock
    // не было ниже блока controls соседней карточки и шапки
    // upd: увеличил z-index, чтобы перекрыть рекламу у эшншитов на мобилке
    &#{$controls} {
      &--active {
        z-index: 1000000;
      }
    }
  }

  .lh-article-card-controls__comments {
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    color: var(--card-controls-text-color, #5c5c5c);

    .icon {
      color: var(--card-controls-color, #969698);
    }

    @include hover {
      color: var(--card-controls-text-hover-color, black);

      .icon {
        color: var(--card-controls-hover-color, black);
      }
    }
  }

  .lh-article-card-controls__favorite {
    margin-right: 12px;
    display: flex;
  }

  .lh-article-card-controls__comments {
    display: flex;
    align-items: center;
    margin-right: 12px;

    span {
      margin-left: 2px;
      font-weight: 700;
    }
  }
</style>
